import React, { useState, useCallback } from 'react';
import { Box, IconButton, Tooltip, CircularProgress } from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { BarcodeScannerModal } from '../../pages/pos/components/BarcodeScannerModal';
import { BarcodeNotFoundDialog } from './BarcodeNotFoundDialog';
import { BarcodeLinkModal } from './BarcodeLinkModal';
import { InlineProductCreateModal } from './InlineProductCreateModal';
import { searchProductByBarcode } from '../../hooks/useBarcodeProductSearch';
import type { Product, BarcodeSearchResponse } from '../../types';

interface POSColors {
  bg: string;
  surface: string;
  surfaceLight: string;
  border: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accent: string;
  accentLight: string;
}

// Default colors for non-POS contexts
const defaultColors: POSColors = {
  bg: '#F8FAFC',
  surface: '#FFFFFF',
  surfaceLight: '#F1F5F9',
  border: '#E2E8F0',
  textPrimary: '#1E293B',
  textSecondary: '#475569',
  textMuted: '#94A3B8',
  accent: '#2563EB',
  accentLight: '#3B82F6',
};

export interface BarcodeProductSearchProps {
  /**
   * Callback when a product is found (either by scanning, linking, or creating)
   * @param product - The product that was found/created
   * @param barcode - The barcode that was scanned
   */
  onProductFound: (product: Product, barcode: string) => void;
  /**
   * Optional callback when barcode lookup fails (network error, etc.)
   * If not provided, errors are handled internally
   */
  onError?: (error: Error, barcode: string) => void;
  /**
   * Enable continuous scanning mode - scanner stays open after each scan
   * Useful for adding multiple products quickly (e.g., restock page)
   * @default false
   */
  continuousMode?: boolean;
  /**
   * Optional location ID for inventory lookup
   * When provided, product will include inventoryAtLocation data
   */
  locationId?: string;
  /**
   * Optional theme colors (for POS context)
   */
  colors?: POSColors;
  /**
   * Whether to show the scanner button (vs just providing programmatic control)
   * @default true
   */
  showButton?: boolean;
  /**
   * Size of the scanner button
   * @default 'medium'
   */
  buttonSize?: 'small' | 'medium' | 'large';
  /**
   * Custom button styles
   */
  buttonSx?: object;
  /**
   * Tooltip text for the scanner button
   * @default 'Scanner un code-barres'
   */
  buttonTooltip?: string;
  /**
   * Whether the scanner is disabled
   * @default false
   */
  disabled?: boolean;
}

type DialogState = 'closed' | 'scanner' | 'not-found' | 'link' | 'create';

/**
 * BarcodeProductSearch - Orchestrator component for barcode scanning workflow
 *
 * This component combines:
 * - BarcodeScannerModal: Camera-based barcode scanning
 * - Barcode lookup: API call to find product by barcode
 * - BarcodeNotFoundDialog: Options when barcode not in system
 * - BarcodeLinkModal: Link barcode to existing product
 * - InlineProductCreateModal: Create new product with barcode
 *
 * @example
 * ```tsx
 * // Basic usage
 * <BarcodeProductSearch
 *   onProductFound={(product, barcode) => {
 *     // Add product to cart, order, etc.
 *   }}
 * />
 *
 * // Continuous mode for bulk scanning
 * <BarcodeProductSearch
 *   onProductFound={handleProductFound}
 *   continuousMode={true}
 *   locationId={selectedLocationId}
 * />
 *
 * // With custom styling
 * <BarcodeProductSearch
 *   onProductFound={handleProductFound}
 *   buttonSize="large"
 *   buttonSx={{ color: 'primary.main' }}
 * />
 * ```
 */
export const BarcodeProductSearch: React.FC<BarcodeProductSearchProps> = ({
  onProductFound,
  onError,
  continuousMode = false,
  locationId,
  colors = defaultColors,
  showButton = true,
  buttonSize = 'medium',
  buttonSx,
  buttonTooltip = 'Scanner un code-barres',
  disabled = false,
}) => {
  // Dialog state management
  const [dialogState, setDialogState] = useState<DialogState>('closed');
  const [currentBarcode, setCurrentBarcode] = useState<string>('');
  const [isLookingUp, setIsLookingUp] = useState(false);

  /**
   * Open the barcode scanner modal
   * Can be called programmatically via ref or from the button
   */
  const openScanner = useCallback(() => {
    setDialogState('scanner');
  }, []);

  /**
   * Close all dialogs and reset state
   */
  const closeAll = useCallback(() => {
    setDialogState('closed');
    setCurrentBarcode('');
    setIsLookingUp(false);
  }, []);

  /**
   * Handle barcode scan from the scanner modal
   * Looks up the barcode and shows appropriate UI based on result
   */
  const handleBarcodeScan = useCallback(
    async (barcode: string) => {
      if (!barcode || barcode.trim() === '') {
        return;
      }

      const trimmedBarcode = barcode.trim();
      setCurrentBarcode(trimmedBarcode);
      setIsLookingUp(true);

      try {
        const result: BarcodeSearchResponse = await searchProductByBarcode(
          trimmedBarcode,
          locationId
        );

        if (result.found && result.product) {
          // Product found - call callback
          onProductFound(result.product, trimmedBarcode);

          // In continuous mode, keep scanner open
          // In single mode, close everything
          if (!continuousMode) {
            closeAll();
          }
        } else {
          // Product not found - show not-found dialog
          // Close scanner first, then show not-found dialog
          setDialogState('not-found');
        }
      } catch (error) {
        // Handle lookup error
        if (onError) {
          onError(error as Error, trimmedBarcode);
        }
        // Show not-found dialog as fallback (user can still link/create)
        setDialogState('not-found');
      } finally {
        setIsLookingUp(false);
      }
    },
    [locationId, onProductFound, onError, continuousMode, closeAll]
  );

  /**
   * Handle user choosing to link barcode to existing product
   */
  const handleLinkToExisting = useCallback(() => {
    setDialogState('link');
  }, []);

  /**
   * Handle user choosing to create new product
   */
  const handleCreateNew = useCallback(() => {
    setDialogState('create');
  }, []);

  /**
   * Handle successful barcode linking
   * Product now has the barcode linked - call callback
   */
  const handleBarcodeLinked = useCallback(
    (product: Product) => {
      onProductFound(product, currentBarcode);
      closeAll();
    },
    [onProductFound, currentBarcode, closeAll]
  );

  /**
   * Handle successful product creation
   * New product was created with the barcode - call callback
   */
  const handleProductCreated = useCallback(
    (product: Product) => {
      onProductFound(product, currentBarcode);
      closeAll();
    },
    [onProductFound, currentBarcode, closeAll]
  );

  /**
   * Handle closing the not-found dialog
   * Go back to scanner if in continuous mode, otherwise close all
   */
  const handleNotFoundClose = useCallback(() => {
    if (continuousMode) {
      setDialogState('scanner');
    } else {
      closeAll();
    }
  }, [continuousMode, closeAll]);

  /**
   * Handle closing the link/create modals
   * Go back to not-found dialog to allow choosing the other option
   */
  const handleSubModalClose = useCallback(() => {
    setDialogState('not-found');
  }, []);

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      {/* Scanner trigger button */}
      {showButton && (
        <Tooltip title={buttonTooltip}>
          <span>
            <IconButton
              onClick={openScanner}
              disabled={disabled || isLookingUp}
              size={buttonSize}
              sx={{
                color: colors.accent,
                '&:hover': {
                  backgroundColor: `${colors.accent}15`,
                },
                '&.Mui-disabled': {
                  color: colors.textMuted,
                },
                ...buttonSx,
              }}
            >
              {isLookingUp ? (
                <CircularProgress size={buttonSize === 'small' ? 18 : 24} />
              ) : (
                <QrCodeScannerIcon />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}

      {/* Barcode Scanner Modal */}
      <BarcodeScannerModal
        open={dialogState === 'scanner'}
        onClose={closeAll}
        onScan={handleBarcodeScan}
        continuousMode={continuousMode}
      />

      {/* Barcode Not Found Dialog */}
      <BarcodeNotFoundDialog
        open={dialogState === 'not-found'}
        onClose={handleNotFoundClose}
        barcode={currentBarcode}
        onLinkToExisting={handleLinkToExisting}
        onCreateNew={handleCreateNew}
        colors={colors}
      />

      {/* Link Barcode to Existing Product Modal */}
      <BarcodeLinkModal
        open={dialogState === 'link'}
        onClose={handleSubModalClose}
        barcode={currentBarcode}
        onLinked={handleBarcodeLinked}
        colors={colors}
      />

      {/* Create New Product Modal */}
      <InlineProductCreateModal
        open={dialogState === 'create'}
        onClose={handleSubModalClose}
        barcode={currentBarcode}
        onProductCreated={handleProductCreated}
        colors={colors}
      />
    </Box>
  );
};

// Export ref-based control interface for programmatic usage
export interface BarcodeProductSearchRef {
  openScanner: () => void;
  closeAll: () => void;
}

export default BarcodeProductSearch;
